import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Form, {
  AsyncRule,
  ButtonItem,
  EmptyItem,
  FormRef,
  GroupItem,
  Label,
  RequiredRule,
  SimpleItem,
} from "devextreme-react/form";
import { resource, run, sleep } from "effection";
import sum from "hash-sum";
import { useTranslation } from "react-i18next";

import {
  selectCurrentGroupsTreeListId,
  selectedGroupData,
  loadOrganizationCommercialProfile,
  updateOrganizationCommercialProfile,
  updateSuGroup,
  useAppDispatch,
  useAppSelector,
} from "@app/state";
import { commonStyle } from "@app/style/commonForms";

import type { Operation } from "effection";
import type dxForm from "devextreme-react/form";
import type { IGroupMember } from "@app/types";

function defer(op: () => Operation<unknown>): Operation<void> {
  return resource(function* (provide) {
    try {
      yield* provide();
    } finally {
      yield* op();
    }
  });
}

export function OrgFormData({ readOnly = false }: { readOnly?: boolean }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const effRef5 = useRef<boolean>(false);
  const formformref = useRef<FormRef>(null);

  const [formDataSource, setFormDataSource] = useState<IGroupMember | null>(
    null
  );
  const theSelectedTreeId = useAppSelector(
    selectCurrentGroupsTreeListId
  ) as number;
  const theSelectedOrgData = useAppSelector((s) =>
    selectedGroupData(s, theSelectedTreeId)
  );
  const commonTextEditOptions = {
    ...commonStyle({ elementclass: "filledunderline" }),
  };

  const dataCrc = useMemo(() => sum(theSelectedOrgData), [theSelectedOrgData]);

  const eLoadDataset = function* (): Operation<boolean> {
    try {
      formformref.current?.instance().beginUpdate();
      setFormDataSource({ ...theSelectedOrgData });
      yield* sleep(1);
    } finally {
      formformref.current?.instance().endUpdate();
    }
    return true;
  };

  function* eUpdateDataset(data: IGroupMember): Operation<void> {
    try {
      formformref.current?.instance().beginUpdate();
      yield* sleep(1);
    } finally {
      dispatch(updateSuGroup(data));
      formformref.current?.instance().endUpdate();
    }
    return;
  }

  useEffect(() => {
    run(eLoadDataset);
  }, [dataCrc]);

  useEffect(() => {
    if (
      theSelectedOrgData.GROUPID > 0 &&
      theSelectedOrgData.PERSEED &&
      !theSelectedOrgData.commercialProfile
    ) {
      dispatch(
        loadOrganizationCommercialProfile({
          GROUPID: theSelectedOrgData.GROUPID,
          PERSEED: theSelectedOrgData.PERSEED,
        })
      );
    }
  }, [
    dispatch,
    theSelectedOrgData.GROUPID,
    theSelectedOrgData.PERSEED,
    theSelectedOrgData.commercialProfile,
  ]);

  useEffect(() => {
    effRef5.current = true;
    return () => {
      effRef5.current = false;
    };
  });

  const perseedEditorOptions = useMemo(
    () => ({
      ...commonTextEditOptions,
      readOnly: true,
    }),
    []
  );

  const saveCommercialProfile = useCallback(() => {
    const validation = formformref.current?.instance().validate();
    if (!validation?.isValid) return;
    const formData = formformref.current
      ?.instance()
      .option("formData") as IGroupMember;
    if (!formData?.commercialProfile) return;
    dispatch(
      updateOrganizationCommercialProfile({
        GROUPID: theSelectedOrgData.GROUPID,
        PERSEED: theSelectedOrgData.PERSEED,
        profile: formData.commercialProfile,
      })
    );
  }, [
    dispatch,
    theSelectedOrgData.GROUPID,
    theSelectedOrgData.PERSEED,
  ]);

  const fieldDataChanged = (e: any) => {
    const { dataField } = e || {};
    let value = e?.value;
    if (!dataField) return;
    value = typeof value === "string" ? value.trim() : value;
    if (value === "") {
      value = null;
    }
    const isValid = formformref.current?.instance().validate().isValid;
    if (!isValid) {
      return;
    }
    if (dataField.startsWith("commercialProfile.")) {
      return;
    }
    run(eLoadDataset);
    const prevData = structuredClone(theSelectedOrgData);
    if (prevData[dataField as keyof IGroupMember] === value) return;
    const newData = { ...prevData, [dataField]: value, touched: true };
    run(() => eUpdateDataset(newData));
  };

  return (
    <div id={"orgdata"}>
      <Form
        ref={formformref}
        id={"formformOrgData"}
        className={"form-form org-data"}
        formData={formDataSource}
        showColonAfterLabel={true}
        labelLocation={"left"}
        colCount={1}
        width={"100%"}
        onFieldDataChanged={fieldDataChanged}
        readOnly={readOnly}
        showValidationSummary={true}
        validationGroup="createGroupForm"
      >
        <GroupItem caption={t("Organization Data")} colCount={4}>
          <SimpleItem
            dataField={"GROUP_NAME"}
            editorType={"dxTextBox"}
            editorOptions={commonTextEditOptions}
            colSpan={4}
          >
            <Label text={t("GroupName")} />
            <RequiredRule message={t("GroupNameRequiredMessage")} />
          </SimpleItem>

          <SimpleItem
            dataField={"PERSEED"}
            editorType={"dxTextBox"}
            editorOptions={perseedEditorOptions}
            colSpan={2}
          >
            <Label text={t("Perseed")} />
            <RequiredRule message={t("PerseedRequiredMessage")} />
          </SimpleItem>
          <EmptyItem />
        </GroupItem>

        <GroupItem
          caption="SWS Commercial Identity"
          colCount={4}
          visible={Boolean(theSelectedOrgData.commercialProfile)}
        >
          <SimpleItem
            dataField="commercialProfile.commercial.legalName"
            editorType="dxTextBox"
            editorOptions={commonTextEditOptions}
            colSpan={4}
          >
            <Label text="Commercial name" />
          </SimpleItem>
          <SimpleItem
            dataField="commercialProfile.commercial.fiscalCode"
            editorType="dxTextBox"
            editorOptions={commonTextEditOptions}
            colSpan={2}
          >
            <Label text="CIF/CUI" />
          </SimpleItem>
          <SimpleItem
            dataField="commercialProfile.commercial.tradeRegisterNumber"
            editorType="dxTextBox"
            editorOptions={commonTextEditOptions}
            colSpan={2}
          >
            <Label text="REGCOM" />
          </SimpleItem>
          <SimpleItem
            dataField="commercialProfile.commercial.address"
            editorType="dxTextBox"
            editorOptions={commonTextEditOptions}
            colSpan={4}
          >
            <Label text="Address" />
          </SimpleItem>
          <SimpleItem
            dataField="commercialProfile.commercial.city"
            editorType="dxTextBox"
            editorOptions={commonTextEditOptions}
            colSpan={2}
          >
            <Label text="City" />
          </SimpleItem>
          <SimpleItem
            dataField="commercialProfile.commercial.county"
            editorType="dxTextBox"
            editorOptions={commonTextEditOptions}
            colSpan={2}
          >
            <Label text="County" />
          </SimpleItem>
          <SimpleItem
            dataField="commercialProfile.commercial.countryCode"
            editorType="dxTextBox"
            editorOptions={commonTextEditOptions}
            colSpan={2}
          >
            <Label text="Country code" />
          </SimpleItem>
          <SimpleItem
            dataField="commercialProfile.commercial.email"
            editorType="dxTextBox"
            editorOptions={commonTextEditOptions}
            colSpan={2}
          >
            <Label text="Email" />
          </SimpleItem>
        </GroupItem>

        <GroupItem
          caption="Fiscal Invoicing Identity"
          colCount={4}
          visible={Boolean(theSelectedOrgData.commercialProfile)}
        >
          <SimpleItem
            dataField="commercialProfile.fiscal.legalName"
            editorType="dxTextBox"
            editorOptions={commonTextEditOptions}
            colSpan={4}
          >
            <Label text="Fiscal name" />
          </SimpleItem>
          <SimpleItem
            dataField="commercialProfile.fiscal.fiscalCode"
            editorType="dxTextBox"
            editorOptions={commonTextEditOptions}
            colSpan={2}
          >
            <Label text="CIF/CUI" />
          </SimpleItem>
          <SimpleItem
            dataField="commercialProfile.fiscal.tradeRegisterNumber"
            editorType="dxTextBox"
            editorOptions={commonTextEditOptions}
            colSpan={2}
          >
            <Label text="REGCOM" />
          </SimpleItem>
          <SimpleItem
            dataField="commercialProfile.fiscal.address"
            editorType="dxTextBox"
            editorOptions={commonTextEditOptions}
            colSpan={4}
          >
            <Label text="Billing address" />
          </SimpleItem>
          <SimpleItem
            dataField="commercialProfile.fiscal.city"
            editorType="dxTextBox"
            editorOptions={commonTextEditOptions}
            colSpan={2}
          >
            <Label text="City" />
          </SimpleItem>
          <SimpleItem
            dataField="commercialProfile.fiscal.county"
            editorType="dxTextBox"
            editorOptions={commonTextEditOptions}
            colSpan={2}
          >
            <Label text="County" />
          </SimpleItem>
          <SimpleItem
            dataField="commercialProfile.fiscal.countyCode"
            editorType="dxTextBox"
            editorOptions={commonTextEditOptions}
            colSpan={2}
          >
            <Label text="County code" />
          </SimpleItem>
          <SimpleItem
            dataField="commercialProfile.fiscal.countryCode"
            editorType="dxTextBox"
            editorOptions={commonTextEditOptions}
            colSpan={2}
          >
            <Label text="Country code" />
          </SimpleItem>
          <SimpleItem
            dataField="commercialProfile.fiscal.email"
            editorType="dxTextBox"
            editorOptions={commonTextEditOptions}
            colSpan={2}
          >
            <Label text="Billing email" />
          </SimpleItem>
          <SimpleItem
            dataField="commercialProfile.fiscal.usesRomanianEInvoicing"
            editorType="dxCheckBox"
            colSpan={2}
          >
            <Label text="Uses RO e-Factura" />
          </SimpleItem>
          <ButtonItem
            colSpan={4}
            horizontalAlignment="right"
            buttonOptions={{
              text: "Save commercial and fiscal identity",
              type: "default",
              visible: Boolean(
                theSelectedOrgData.commercialProfile && !readOnly
              ),
              onClick: saveCommercialProfile,
            }}
          />
        </GroupItem>
      </Form>
    </div>
  );
}
